import { z } from 'zod';
import { categoriaDeAvisoSchema, severidadPercibidaSchema } from '../avisos.js';
import { instrumentoSchema } from '../eventos.js';
import { faseSchema } from '../programa/fases.js';
import { claseDeItemSchema } from '../programa/protocolo.js';
/**
 * Contrato del panel del paciente · P-03, P-07, P-08, P-13.
 *
 * Tres cosas que este contrato **no** lleva, y las tres son decisiones:
 *
 *   1. **El semáforo clínico no viaja al paciente.** Es información del
 *      seguimiento para el equipo (`semaforos-etiquetas.md` §1: quién lo ve son
 *      el equipo médico y el coordinador). Mostrarle un rojo a un adulto mayor
 *      que no puede hacer nada con esa información es angustia sin utilidad.
 *   2. **Ningún campo afirma cómo está el paciente.** La card del home refleja
 *      lo que él mismo respondió, entre comillas y con su fecha; no lo evalúa.
 *      Es la resolución de C-16 (`semaforos-etiquetas.md` v1.3): afirmarle el
 *      bienestar a alguien sin que lo haya dicho es invalidante, y clínicamente
 *      riesgoso el día que la afirmación no coincide con cómo se siente.
 *   3. **Nada de PII.** El alias operativo, no el nombre. El nombre vive en el
 *      `Patient` de FHIR.
 */
// ── P-03 · Home ─────────────────────────────────────────────────────────────
/**
 * La card del home · **un reflejo, no un diagnóstico**.
 *
 * `reflejo` devuelve la última respuesta cerrada del propio paciente a la
 * pregunta `movilidad` de su microcheck: **el código de la opción y de qué
 * formulario salió**, con su fecha. La frase la arma el copy de la PWA
 * (ADR-016), porque la pregunta no es la misma en los dos microchecks: el
 * semanal compara con la semana pasada (mejor, igual, peor) y el mensual
 * pregunta por las actividades del día a día (todas, la mayoría, pocas, casi
 * ninguna). Un código que el copy no conoce no se muestra.
 *
 * `sin-dato` es el estado honesto de quien todavía no respondió nada: el home
 * muestra el día, la fase y los pendientes, y nada más.
 */
export const bienestarDelHomeSchema = z.discriminatedUnion('clase', [
    z.strictObject({ clase: z.literal('sin-dato') }),
    z.strictObject({
        clase: z.literal('reflejo'),
        /** El formulario del que salió (`microcheck-semanal`, `microcheck-mensual`…). */
        formularioId: z.string().max(40),
        /** El código de la opción que eligió. */
        codigo: z.string().max(40),
        respondidoAt: z.iso.datetime(),
    }),
]);
export const pendienteDeHoySchema = z.strictObject({
    itemId: z.uuid(),
    clase: claseDeItemSchema,
    hito: z.string().max(20),
    /**
     * El formulario que no es una escala: el microcheck del check-in, o el
     * cuestionario de hito de la evaluación (E3.25). Nulo en las evaluaciones de
     * M2 y M3, que sólo administran escalas.
     */
    formularioId: z.string().max(40).nullable(),
    /**
     * Si ese formulario ya está completo. El check-in lleva además el NMS (D-02):
     * el que respondió el microcheck y salió antes del NMS vuelve directo al NMS.
     * La evaluación va al revés: las escalas y después el cuestionario, y el que
     * salió entre las dos cosas vuelve por lo que le falta.
     */
    formularioRespondido: z.boolean(),
    /** Las escalas del ítem: las de la evaluación, o el NMS del check-in. */
    instrumentos: z.array(instrumentoSchema),
    /**
     * De esos instrumentos, los que el paciente **ya respondió** en este ítem
     * (P-80). Una evaluación interrumpida retoma por el primero que falta, en el
     * orden del protocolo, en vez de volver a preguntar lo ya contestado.
     */
    respondidos: z.array(instrumentoSchema),
    venceAt: z.iso.datetime(),
});
export const inicioDelPacienteSchema = z.strictObject({
    programaId: z.uuid(),
    alias: z.string().max(80),
    diaDelPrograma: z.number().int().nonnegative(),
    totalDeDias: z.number().int().positive(),
    fase: faseSchema,
    bienestar: bienestarDelHomeSchema,
    /**
     * Lo próximo que el paciente va a responder: un check-in o una evaluación.
     * **Sin el código del hito** (`M2`, `D+90`): es un identificador del
     * protocolo, y al paciente se le habla en su idioma (E3.11). `enDias` es de
     * calendario: 0 es hoy. `null` cuando ya no queda ninguno.
     */
    proximoContacto: z
        .strictObject({
        clase: claseDeItemSchema,
        fecha: z.iso.datetime(),
        enDias: z.number().int().nonnegative(),
    })
        .nullable(),
    pendientes: z.array(pendienteDeHoySchema),
});
// ── P-08 · Mi progreso ──────────────────────────────────────────────────────
/**
 * Un punto del recorrido.
 *
 * `estado` sale del plan y del calendario, no de un juicio: `cumplido` es un
 * ítem que se respondió, `en-curso` el de hoy, `pendiente` lo que viene, y
 * `sin-responder` lo que venció. **No hay color.** El paciente ve su recorrido,
 * no su evaluación.
 */
export const hitoDelRecorridoSchema = z.strictObject({
    itemId: z.uuid(),
    codigo: z.string().max(40),
    clase: claseDeItemSchema,
    hito: z.string().max(20),
    diaRelativo: z.number().int().nonnegative(),
    fecha: z.iso.datetime(),
    estado: z.enum(['cumplido', 'en-curso', 'pendiente', 'sin-responder', 'excluido']),
});
export const progresoDelPacienteSchema = z.strictObject({
    diaDelPrograma: z.number().int().nonnegative(),
    totalDeDias: z.number().int().positive(),
    fase: faseSchema,
    recorrido: z.array(hitoDelRecorridoSchema),
});
// ── P-13 · Perfil ───────────────────────────────────────────────────────────
export const perfilDelPacienteSchema = z.strictObject({
    alias: z.string().max(80),
    institucion: z.string().max(120),
    diaDelPrograma: z.number().int().nonnegative(),
    fase: faseSchema,
    /** Fecha de la cirugía, si el paciente la confirmó en el onboarding. */
    cirugiaAt: z.iso.date().nullable(),
    pushHabilitado: z.boolean().nullable(),
    /** El cuidador vinculado, por su alias. Nunca su teléfono. */
    cuidador: z.strictObject({ alias: z.string().max(80), vinculo: z.string().max(40) }).nullable(),
});
// ── P-07 · Reportar un evento (UC-P06) ──────────────────────────────────────
/**
 * El reporte del paciente · **estructurado y nada más**.
 *
 * El wireframe P-07 tiene un campo "Contanos más (opcional)" de texto libre. No
 * está en este contrato, y no es un olvido: el payload de `patient.event_reported`
 * es cerrado y sin narrativa, porque estructurar automáticamente lo que el
 * paciente escribe es exactamente lo diferido en `CLAUDE.md` §4.1. Un campo cuyo
 * contenido no va a ningún lado es peor que no tenerlo — el paciente cuenta algo
 * creyendo que alguien lo va a leer.
 *
 * Lo que sí viaja: la categoría de una lista cerrada, cuándo fue y qué tan fuerte
 * le pareció. Con eso el equipo tiene el hecho, y la conversación es de PIA.
 */
export const CUANDO_DEL_REPORTE = ['ahora', 'hoy', 'ayer', 'esta-semana'];
export const cuandoDelReporteSchema = z.enum(CUANDO_DEL_REPORTE);
export const reportarEventoSchema = z.strictObject({
    categoria: categoriaDeAvisoSchema,
    severidadPercibida: severidadPercibidaSchema.nullable(),
    cuando: cuandoDelReporteSchema,
});
export const reporteRegistradoSchema = z.strictObject({
    eventoId: z.uuid(),
    categoria: z.string().max(40),
    occurredAt: z.iso.datetime(),
});
// ── Lo que contó en el año (E3.25, D-01) ────────────────────────────────────
/**
 * Las reinternaciones y reintervenciones que el paciente contó en sus
 * cuestionarios, sin repetir. D+360 se las muestra antes de preguntarle si
 * falta alguna: es el "total en 12 meses" del protocolo, que no se vuelve a
 * preguntar. Fechas y códigos, sin texto.
 */
export const hechosDelPacienteSchema = z.strictObject({
    reinternaciones: z.array(z.strictObject({
        fecha: z.iso.date(),
        lugar: z.enum(['esta-institucion', 'otra-institucion']),
    })),
    reintervenciones: z.array(z.strictObject({ fecha: z.iso.date(), cirugia: z.string().max(40) })),
});
//# sourceMappingURL=paciente.js.map